import {
  type WorkflowStep,
  type WorkflowStepInput,
  type WorkflowStepUser,
  type WorkflowTemplateStep,
  type User,
  WorkflowStepStatuses,
  WorkflowStepUserResponses,
} from "~/api/types";

import { currentUserAtTopCompareFn, printFullName } from "./user";

export type UserDisplayMap = Map<
  number,
  { firstName: string; lastName?: string; imageUrl?: string }
>;

export function buildUserDisplayMap(users: User[] | undefined): UserDisplayMap {
  const map: UserDisplayMap = new Map();
  if (users) {
    for (const u of users) {
      map.set(u.id, {
        firstName: u.firstName,
        lastName: u.lastName,
        imageUrl: u.imageUrl,
      });
    }
  }
  return map;
}

export function mapStepInputsToWorkflowSteps(
  steps: WorkflowStepInput[],
): WorkflowStep[] {
  return steps.map((s, i) => ({
    id: s.id ?? -(i + 1),
    name: s.name,
    description: s.description ?? "",
    type: s.type,
    dueAt: s.dueAt,
    order: i,
    status: WorkflowStepStatuses.upcoming,
    users: s.users,
  }));
}

export function addStepKeys(steps: WorkflowStep[]): WorkflowStepInput[] {
  return steps.map((step) => ({ ...step, _key: `${step.id}` }));
}

export function hydrateTemplateSteps(
  steps: WorkflowTemplateStep[],
  userMap: UserDisplayMap,
): WorkflowStepInput[] {
  return steps.map((step, i) => ({
    _key: `${step.id ?? i}`,
    id: step.id,
    name: step.name,
    description: step.description ?? "",
    type: step.type,
    order: i,
    status: WorkflowStepStatuses.upcoming,
    dueAt: step.dueAt ? new Date(step.dueAt) : null,
    users: step.users.map((u): WorkflowStepUser => {
      const info = userMap.get(u.userId);
      return {
        userId: u.userId,
        firstName: info?.firstName ?? "Unknown",
        lastName: info?.lastName,
        imageUrl: info?.imageUrl,
        permission: u.permission,
        responseStatus: WorkflowStepUserResponses.pending,
      };
    }),
  }));
}

interface ExtractOwnerInput {
  userId: number;
  firstName: string;
  lastName?: string;
}

export function extractOwners(
  docs: ExtractOwnerInput[],
  currentUserId: number,
) {
  const ownerIds = new Set<number>();

  return docs
    .filter(({ userId }) => {
      if (ownerIds.has(userId)) {
        return false;
      } else {
        ownerIds.add(userId);
        return true;
      }
    })
    .toSorted((a, b) =>
      currentUserAtTopCompareFn(a.userId, b.userId, currentUserId),
    )
    .map((owner) => ({
      label: printFullName(owner, owner.userId === currentUserId),
      value: `${owner.userId}`,
    }));
}
